/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { StaticQuery, graphql } from 'gatsby';

// const defaultLangKey = 'en-ca';

function SEO({ description, lang, meta, image, title, location, redirect }) {
  // * Lang is the html-lang of the content on the page
  // * lang is the language that the page should be associated with
  // * if lang and lang dont match, that means we are on a translated paged without translated copy, i.e the copy duplicates that of the main page, so we set a canonical link
  return (
    <StaticQuery
      query={graphql`
        query {
          site {
            siteMetadata {
              title
              description
              author
              siteUrl
            }
          }
          prodigy {
            localeMappings {
              path
              locale
            }
          }
        }
      `}
      render={({ site, prodigy }) => {
        const metaDescription = description || site.siteMetadata.description;

        const path = `${site.siteMetadata.siteUrl}${redirect ||
          (location ? location.pathname : '')}`;

        // * Replace en-ca with en in cannonical tags
        const isDefaultLang = `${lang}` === 'en-ca';

        let alternativeVersions = [];

        if (location) {
          // * Get the current custom locale path on the page
          const currentPrefix = prodigy.localeMappings.find(
            localeData => localeData.locale === lang
          );

          // * Returns an array of alternative page links, based on all locales available
          // **These locales can be found in prismic
          alternativeVersions = prodigy.localeMappings.map(cur => {
            // if (cur.locale !== lang) {
            const alternatePath = `https://www.${path.substr(
              path.indexOf('prodigygame')
            )}`.replace(currentPrefix.path, cur.path);
            return {
              rel: 'alternate',
              href: alternatePath,
              hreflang: `${cur.locale}`,
            };
          });
        }

        return (
          <Helmet
            htmlAttributes={{
              lang: isDefaultLang ? 'en' : `${lang}`,
            }}
            title={title}
            titleTemplate={`%s | ${site.siteMetadata.title}`}
            link={[...alternativeVersions].concat({
              rel: 'canonical',
              href: `https://www.${path.substr(path.indexOf('prodigygame'))}`,
            })}
            meta={[
              {
                name: `description`,
                content: metaDescription,
              },
              {
                property: `og:title`,
                content: title,
              },
              {
                property: `og:description`,
                content: metaDescription,
              },
              {
                property: `og:type`,
                content: `website`,
              },
              {
                property: 'og:image',
                content: image,
              },
              {
                property: 'twitter:image',
                content: image,
              },
              {
                name: `twitter:card`,
                content: `summary`,
              },
              {
                name: `twitter:creator`,
                content: site.siteMetadata.author,
              },
              {
                name: `twitter:title`,
                content: title,
              },
              {
                name: `twitter:description`,
                content: metaDescription,
              },
            ].concat(meta)}
          >
            {/* gtm script */}
            {/* eslint-disable */}
             {process.env.NODE_ENV === 'production' && typeof window !== "undefined" && (
               <script defer>
                 {(function (w, d, s, l, i) {
                   w[l] = w[l] || [];
                   w[l].push({
                     'gtm.start': new Date().getTime(),
                     event: 'gtm.js',
                   });
                   const f = d.getElementsByTagName(s)[0];
                   const j = d.createElement(s);
                   const dl = l != 'dataLayer' ? `&l=${l}` : '';
                   j.defer = true;
                   j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
                   f.parentNode.insertBefore(j, f);
                 })(window, document, 'script', 'dataLayer', 'GTM-T7TSN5K')}
               </script>
             )}
             {process.env.NODE_ENV === 'production' && typeof window !== "undefined" && lang === 'en-in' &&
             /* Load India's GTM container on their version of the website */
               <script defer>
                 {(function (w, d, s, l, i) {
                   w[l] = w[l] || [];
                   w[l].push({
                     'gtm.start': new Date().getTime(),
                     event: 'gtm.js',
                   });
                   const f = d.getElementsByTagName(s)[0];
                   const j = d.createElement(s);
                   const dl = l != 'dataLayer' ? `&l=${l}` : '';
                   j.defer = true;
                   j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
                   f.parentNode.insertBefore(j, f);
                 })(window, document, 'script', 'dataLayer', 'GTM-K4F3DTH')}
               </script>
             }
             {/* eslint-enable */}
          </Helmet>
        );
      }}
    />
  );
}

SEO.defaultProps = {
  lang: `en-ca`,
  meta: [],
  description: ``,
  location: null,
  image: `https://webcdn.prodigygame.com/common/images/socialshare.png`,
  redirect: false,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  location: PropTypes.shape(),
  image: PropTypes.string,
  redirect: PropTypes.string,
};

export default SEO;
